import { format } from "date-fns";
import { ReactNode } from "react";

import Icons from "@/components/icons";
import { DateTimeCell } from "@/components/ui/v1/table/TableCells";
import { Badge } from "@/components/ui/v2/badge";
import type { ModuleUiColumnDefinition } from "@/features/integrations/types";
import { type ValueFormat, formatValue } from "@/utils/valueFormat";

/** Backend DataTableColumnType (String, Int, Float, …) to a frontend cell-rendering key. */
function normalizeColumnType(type: string): string {
  const map: Record<string, string> = {
    string: "text",
    date: "date",
    formatteddate: "formatteddate",
    datetime: "datetime",
    int: "whole",
    float: "decimal",
    // Legacy alias still live in the job-sync schemas, matching the backend's Number → Int.
    number: "whole",
    currency: "currency",
    percentage: "percentage",
    badge: "badge",
    boolean: "boolean",
    link: "link",
  };
  // Lowercased: seeded schemas carry both "Number" and "number", and the backend parses its enum
  // case-insensitively.
  return map[type?.toLowerCase()] ?? type;
}

/** Plain-text cell rendering (the `text`/default case), reused by the `link` fallback. */
function renderTextCell(
  value: unknown,
  column: ModuleUiColumnDefinition,
  translate: (key: string, options?: Record<string, unknown>) => string
): ReactNode {
  const lowerKey = column.key.toLowerCase();
  const isIdField = lowerKey.includes("id");
  const isNameField = lowerKey.includes("name");

  if (value == null || value === "") {
    return (
      <span className="text-muted-foreground">
        {emptyDisplay(column.showNullAs, "-", translate)}
      </span>
    );
  }

  if (isIdField) {
    return <span className="font-mono text-sm">{String(value)}</span>;
  }

  if (isNameField) {
    return <span className="font-medium">{String(value)}</span>;
  }

  return <span>{String(value)}</span>;
}

/** Resolves the column's configured display for an absent value ("N/A" resolves via i18n). */
export function emptyDisplay(
  configured: string | undefined,
  fallback: string | undefined,
  translate: (key: string, options?: Record<string, unknown>) => string,
): string | undefined {
  if (configured === undefined) return fallback;
  return configured === "N/A"
    ? translate("common.na", { defaultValue: "N/A" })
    : configured;
}

/** Resolves a column's alignment class; numeric/boolean columns pass their own default. */
function alignmentClass(
  textAlign: ModuleUiColumnDefinition["textAlign"],
  fallback: "text-left" | "text-center" | "text-right",
): string {
  if (textAlign === "left") return "text-left";
  if (textAlign === "center") return "text-center";
  if (textAlign === "right") return "text-right";
  return fallback;
}

/**
 * A true/false status, shown as a check or a dash rather than the word. The icon is the formatting,
 * so this lives here and not in valueFormat (which returns strings and is used outside React).
 */
function renderBooleanCell(
  value: unknown,
  column: ModuleUiColumnDefinition,
  translate: (key: string, options?: Record<string, unknown>) => string,
): ReactNode {
  const className = alignmentClass(column.textAlign, "text-center");

  // Absent is not false: an unknown status shows the column's empty display, not a dash-as-"no".
  if (value == null || value === "") {
    return (
      <span className={`${className} text-muted-foreground`}>
        {emptyDisplay(column.showNullAs, "-", translate)}
      </span>
    );
  }

  const isTrue = value === true || value === "true" || value === 1;

  return (
    <span className={`${className} block`}>
      {isTrue ? (
        <Icons.CheckIcon
          className="h-5 w-5 inline-block text-success"
          aria-label={translate("common.yes", { defaultValue: "Yes" })}
        />
      ) : (
        <span className="text-muted-foreground" aria-label={translate("common.no", { defaultValue: "No" })}>
          -
        </span>
      )}
    </span>
  );
}

/**
 * The normalized column type as a ValueFormat. Narrowed rather than cast, so an unrecognised type
 * falls back to a sane 2-decimal render instead of being trusted blindly.
 */
function numericFormat(type: string): ValueFormat {
  return type === "whole" || type === "currency" || type === "percentage" ? type : "decimal";
}

/**
 * Every numeric column type (count / decimal / currency / percentage) renders through here, so they
 * share one empty-value contract, one alignment rule and one grouping/decimal policy.
 */
function renderNumericCell(
  value: unknown,
  normalizedType: string,
  column: ModuleUiColumnDefinition,
  translate: (key: string, options?: Record<string, unknown>) => string,
): ReactNode {
  const num =
    value == null || value === ""
      ? null
      : typeof value === "number"
        ? value
        : Number(value);

  const nullDisplay = emptyDisplay(column.showNullAs, "-", translate)!;
  const zeroDisplay = emptyDisplay(column.showZeroAs, undefined, translate);

  // Numeric columns right-align by default so digits line up down the column.
  const alignClass = alignmentClass(column.textAlign, "text-right");

  // Empty cells keep the column's alignment so a sparsely populated column doesn't zig-zag.
  if (num === null || Number.isNaN(num)) {
    return <span className={`${alignClass} text-muted-foreground`}>{nullDisplay}</span>;
  }
  // Zero renders as a number unless the column opts out via showZeroAs — the formatter never
  // decides that, or a measured zero becomes indistinguishable from missing data.
  if (num === 0 && zeroDisplay !== undefined) {
    return <span className={`${alignClass} text-muted-foreground`}>{zeroDisplay}</span>;
  }

  // Never re-scales a percentage: 0.8 is a real 0,80%, not a fraction (OR-2436).
  const formatted = formatValue(num, { format: numericFormat(normalizedType), na: nullDisplay });

  return <span className={`${alignClass} tabular-nums`}>{formatted}</span>;
}

/**
 * Generic cell renderer based on column type - no hardcoded column key checks.
 * Works with any data type - components cast rows to their types.
 */
export function createCellRenderer(
  column: ModuleUiColumnDefinition,
  t: (key: string, options?: Record<string, unknown>) => string
): (value: unknown, row: unknown, tFn?: typeof t) => ReactNode {
  return (value: unknown, row: unknown, tFn?: typeof t): ReactNode => {
    const translate = tFn || t;
    const normalizedType = normalizeColumnType(column.type);

    switch (normalizedType) {
    case "badge": {
      // Use valueMap if configured (e.g. true -> common.active, false -> common.inactive)
      const stringValue =
          value === true
            ? "true"
            : value === false
              ? "false"
              : String(value ?? "");
      const mapping = column.valueMap?.[stringValue];

      if (mapping) {
        const variant = (mapping.variant?.toLowerCase() ?? "neutral") as
            | "default"
            | "success"
            | "warning"
            | "error"
            | "neutral";
        const key = variant === "default" ? "neutral" : variant;
        return (
          <Badge variant={key} size="small">
            {translate(mapping.label)}
          </Badge>
        );
      }

      // Fallback: determine variant based on value patterns
      const lowerValue = stringValue.toLowerCase();
      const isPositive =
          value === true ||
          lowerValue === "true" ||
          lowerValue === "active" ||
          lowerValue === "registered" ||
          lowerValue === "success";
      return (
        <Badge
          variant={
            isPositive ? "success" : "neutral"
          }
          size="small"
        >
          {String(value ?? "")}
        </Badge>
      );
    }

    case "formatteddate": {
      if (!value || (typeof value === "string" && !value.trim())) {
        return <span className="text-muted-foreground">-</span>;
      }
      const formattedTimestamp =
        typeof value === "string"
          ? value
          : value instanceof Date
            ? value.toISOString()
            : String(value);
      if (Number.isNaN(new Date(formattedTimestamp).getTime())) {
        return <span className="text-muted-foreground">-</span>;
      }
      return (
        <DateTimeCell
          timestamp={formattedTimestamp}
          tooltipDateOnly
        />
      );
    }

    case "date":
      if (!value) {
        return (
          <span>{translate("common.never", { defaultValue: "Never" })}</span>
        );
      }
      try {
        const dateValue =
            typeof value === "string" ||
            typeof value === "number" ||
            value instanceof Date
              ? new Date(value as string | number | Date)
              : null;
        if (dateValue && !Number.isNaN(dateValue.getTime())) {
          return <span>{format(dateValue, "PP")}</span>;
        }
        return <span>{String(value)}</span>;
      } catch {
        return <span>{String(value)}</span>;
      }

    case "datetime": {
      if (!value || (typeof value === "string" && !value.trim())) {
        return <span className="text-muted-foreground">-</span>;
      }
      const dateTimeTimestamp =
        typeof value === "string"
          ? value
          : value instanceof Date
            ? value.toISOString()
            : String(value);
      if (isNaN(new Date(dateTimeTimestamp).getTime())) {
        return <span className="text-muted-foreground">-</span>;
      }
      return (
        <DateTimeCell
          timestamp={dateTimeTimestamp}
        />
      );
    }

    case "boolean":
      return renderBooleanCell(value, column, translate);

    case "whole":
    case "decimal":
    case "currency":
    case "percentage":
      return renderNumericCell(value, normalizedType, column, translate);

    case "link": {
      // Render the cell's value as an external link using the URL held by the sibling `hrefKey` field;
      // fall back to plain text when the value or href is missing (integration-agnostic).
      const isEmpty = value == null || value === "";
      const href = column.hrefKey
        ? (row as Record<string, unknown> | undefined)?.[column.hrefKey]
        : undefined;

      if (isEmpty || typeof href !== "string" || href === "") {
        return renderTextCell(value, column, translate);
      }

      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="font-mono text-sm text-primary hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {String(value)}
        </a>
      );
    }

    case "text":
    default:
      return renderTextCell(value, column, translate);
    }
  };
}

/**
 * Process schema columns into TableColumn format.
 * Completely generic - works with any data type.
 *
 * @param columns - Column definitions from schema
 * @param t - Translation function
 * @param accessorMap - Optional module-specific accessor mappings
 * @param customCellRenderers - Optional map of column keys to custom cell renderer functions
 */
